import { createSlice } from '@reduxjs/toolkit';
import { updateLanguageDashboard } from '@utils/functions.js';
import { enLangDashboard } from '@utils/translationEN.js';
import Cookies from 'js-cookie';

export const accountSlice = createSlice({
  name: 'account',
  initialState: {
    userList: [],
    board: null,
    account: null,
    requesting: true,
    error: null,
    // HTTP status behind `error`, when there was one. An expired session and a failure inside
    // api/users both land here, and only the first is fixed by signing in again.
    errorStatus: null,

    userId: null,
    userPicUrl: '',
    profileId: null,
    profilePicUrl: '',
    username: '',
    socialLinksItems: [],

    balance: 0,

    email: '',
    lang: 'en',
    translation: enLangDashboard,
    apiKey: '',
    isShowStatus: false,
    fullName: '',

    templates: [],
    themes: [],
    palettes: [],
    wizardStatus: 0,
    isOpenUnlockModal: false,

    // Settings/Flags from board.notifs
    notifs: [],

    // --- Notifications: list + unread counts ---
    notifications: {
      items: [],
      unreadCount: 0,
      unreadByType: {},
      unreadByEvent: {},
    },

    forward: null,
    isDisplayPreviewStore: false,
    storePreviewProducts: [],

    shareQRCode: '',
    previewQRCode: '',
    announcement: null,
  },
  reducers: {
    accountUpdateProfilePicUrl: (state, action) => {
      state.profilePicUrl = action.payload;
    },
    accountUpdateBalance: (state, action) => {
      state.balance = action.payload;
    },
    accountUpdateUsername: (state, action) => {
      state.username = action.payload;
    },
    accountUpdateRequesting: (state, action) => {
      state.requesting = action.payload;
    },
    accountRequest: (state) => {
      state.requesting = true;
      state.error = null;
    },
    accountStore: (state, { payload }) => {
      state.requesting = false;
      state.error = null;
      state.userList = payload.userList;
      state.board = payload.board;
      state.account = payload.user;
      state.profileId = payload.profileId;
      state.userId = payload.userId;
      state.username = payload.username;
      state.profilePicUrl = payload.profilePicUrl;
      state.email = payload.email;
      state.lang = payload.lang;
      state.translation = updateLanguageDashboard(payload.lang);
      state.userPicUrl = payload.userPicUrl;
      state.apiKey = payload?.apiKey;
      state.fullName = payload?.fullName;

      if (payload.status === 'suspended' && payload.subscriptionStatus === 3) {
        state.isShowStatus = true;
      }

      state.templates = payload?.templates;
      state.themes = payload?.themes;
      state.palettes = payload?.palettes;

      state.forward = payload?.forward;
      state.isDisplayPreviewStore = payload?.isDisplayPreviewStore;
      state.storePreviewProducts = payload?.storePreviewProducts;

      state.shareQRCode = payload?.shareQRCode;
      state.previewQRCode = payload?.previewQRCode;
      state.announcement = payload?.announcement || null;

      state.balance = payload?.board?.wallet ?? 0;

      // Сохраняем настройки board
      if (payload.board && payload.board.notifs) {
        state.notifs = payload.board.notifs;
      }

      // --- Notifications: store items only.
      // Unread counts (unreadCount/unreadByType/unreadByEvent) are intentionally NOT set here:
      // /api/users and /api/profiles/{id}/notifications can disagree, and notificationsRequest()
      // is dispatched right after accountStore — letting it be the single source of truth for
      // counts prevents a brief "1" badge flash on initial load.
      if (payload.notifications) {
        state.notifications.items = Array.isArray(payload.notifications) ? payload.notifications : payload.notifications.items || [];
      }
    },
    accountError: (state, action) => {
      // Accepts either a bare message or { message, status }.
      const payload = action.payload;
      const isDetailed = payload !== null && typeof payload === 'object';
      state.error = isDetailed ? payload.message : payload;
      state.errorStatus = isDetailed ? (payload.status ?? null) : null;
      state.requesting = false;
    },
    accountSetStorage: (state, { payload }) => {
      let profileId;

      if (payload === 'newAccount') {
        profileId = state.userList[state.userList.length - 1].profileId;
      } else {
        profileId = payload || 0;
      }

      const existingAccount = state.userList.find((el) => el.profileId === profileId);
      const updatedAccount = existingAccount ?? state.userList[0];

      Cookies.set('unilinkProfileId', updatedAccount.profileId);
      localStorage.setItem('unilinkProfileId', updatedAccount.profileId);
      localStorage.setItem('unilinkUserId', updatedAccount.userId);
    },
    accountUpdateLang: (state, action) => {
      state.lang = action.payload;
    },
    accountUpdateTranslation: (state, action) => {
      state.translation = action.payload;
    },
    accountUpdateApiKey: (state, action) => {
      state.apiKey = action.payload;
    },
    accountUpdateUserPicUrl: (state, action) => {
      state.userPicUrl = action.payload;
    },
    accountUpdateSocialLinksItems: (state, action) => {
      state.socialLinksItems = action.payload;
    },
    accountUpdateIsShowStatus: (state, action) => {
      state.isShowStatus = action.payload;
    },
    accountUpdatePalettes: (state, action) => {
      state.palettes = action.payload;
    },
    accountUpdateWizardStatus: (state, action) => {
      state.wizardStatus = action.payload;
    },

    accountUpdateIsOpenUnlockModal: (state, action) => {
      state.isOpenUnlockModal = action.payload;
    },

    // Обновляет настройки (board.notifs)
    accountUpdateNotifs: (state, action) => {
      state.notifs = action.payload;
    },

    accountUpdateNotificationsItems: (state, action) => {
      state.notifications.items = action.payload;
    },
    accountUpdateNotificationsUnreadCount: (state, action) => {
      state.notifications.unreadCount = action.payload;
    },
    accountUpdateNotificationsUnreadByType: (state, action) => {
      state.notifications.unreadByType = action.payload;
    },
    accountUpdateNotificationsUnreadByEvent: (state, action) => {
      state.notifications.unreadByEvent = action.payload;
    },
    accountPrependNotification: (state, action) => {
      const notification = action.payload;
      // Prevent duplicates
      if (state.notifications.items.some((n) => n.id === notification.id)) return;
      state.notifications.items.unshift(notification);
      // Cap at 100 items
      if (state.notifications.items.length > 100) {
        state.notifications.items = state.notifications.items.slice(0, 100);
      }
      if (!notification.isRead) {
        state.notifications.unreadCount += 1;
        if (notification.notificationType) {
          state.notifications.unreadByType[notification.notificationType] = (state.notifications.unreadByType[notification.notificationType] || 0) + 1;
        }
        if (notification.notificationEvent) {
          state.notifications.unreadByEvent[notification.notificationEvent] = (state.notifications.unreadByEvent[notification.notificationEvent] || 0) + 1;
        }
      }
    },
    accountMarkNotificationRead: (state, action) => {
      const id = action.payload;
      const item = state.notifications.items.find((n) => n.id === id);
      if (item && !item.isRead) {
        item.isRead = true;
        item.readAt = new Date().toISOString();
        state.notifications.unreadCount = Math.max(0, state.notifications.unreadCount - 1);
        if (item.notificationType && state.notifications.unreadByType[item.notificationType]) {
          state.notifications.unreadByType[item.notificationType] -= 1;
        }
        if (item.notificationEvent && state.notifications.unreadByEvent[item.notificationEvent]) {
          state.notifications.unreadByEvent[item.notificationEvent] -= 1;
        }
      }
    },
    accountMarkAllNotificationsRead: (state) => {
      state.notifications.items.forEach((n) => {
        if (!n.isRead) {
          n.isRead = true;
          n.readAt = new Date().toISOString();
        }
      });
      state.notifications.unreadCount = 0;
      Object.keys(state.notifications.unreadByType).forEach((k) => {
        state.notifications.unreadByType[k] = 0;
      });
      Object.keys(state.notifications.unreadByEvent).forEach((k) => {
        state.notifications.unreadByEvent[k] = 0;
      });
    },
    accountDeleteNotification: (state, action) => {
      const id = action.payload;
      const idx = state.notifications.items.findIndex((n) => n.id === id);
      if (idx !== -1) {
        const item = state.notifications.items[idx];
        if (!item.isRead) {
          state.notifications.unreadCount = Math.max(0, state.notifications.unreadCount - 1);
          if (item.notificationType && state.notifications.unreadByType[item.notificationType]) {
            state.notifications.unreadByType[item.notificationType] -= 1;
          }
        }
        state.notifications.items.splice(idx, 1);
      }
    },
    // Saga trigger actions
    notificationsRequest: () => {},
    notificationsRefreshUnread: () => {},

    accountUpdateBoard: (state, action) => {
      state.board = action.payload;
    },
    accountResetMessageNotifications: (state) => {
      if (state.notifications.unreadByEvent['messages'] !== undefined) {
        state.notifications.unreadCount = Math.max(0, state.notifications.unreadCount - state.notifications.unreadByEvent['messages']);
        state.notifications.unreadByEvent['messages'] = 0;
      }
      if (state.notifications.unreadByEvent['new-message'] !== undefined) {
        state.notifications.unreadCount = Math.max(0, state.notifications.unreadCount - state.notifications.unreadByEvent['new-message']);
        state.notifications.unreadByEvent['new-message'] = 0;
      }
    },
    accountUpdateForward: (state, action) => {
      state.forward = action.payload;
    },
    accountUpdateIsDisplayPreviewStore: (state, action) => {
      state.isDisplayPreviewStore = action.payload;
    },
    accountUpdateStorePreviewProducts: (state, action) => {
      state.storePreviewProducts = action.payload;
    },
  },
});

export const {
  accountUpdateProfilePicUrl,
  accountUpdateBalance,
  accountUpdateUsername,
  accountUpdateRequesting,
  accountRequest,
  accountStore,
  accountError,
  accountSetStorage,
  accountUpdateLang,
  accountUpdateTranslation,
  accountUpdateApiKey,
  accountUpdateUserPicUrl,
  accountUpdateSocialLinksItems,
  accountUpdateIsShowStatus,
  accountUpdatePalettes,
  accountUpdateWizardStatus,
  accountUpdateIsOpenUnlockModal,
  accountUpdateNotifs, // Settings
  accountUpdateNotificationsItems, // Messages list
  accountUpdateNotificationsUnreadCount,
  accountUpdateNotificationsUnreadByType,
  accountUpdateNotificationsUnreadByEvent,
  accountPrependNotification,
  accountMarkNotificationRead,
  accountMarkAllNotificationsRead,
  accountDeleteNotification,
  notificationsRequest,
  notificationsRefreshUnread,
  accountUpdateBoard,
  accountResetMessageNotifications,
  accountUpdateForward,
  accountUpdateIsDisplayPreviewStore,
  accountUpdateStorePreviewProducts,
} = accountSlice.actions;

// Selectors
export const selectAccountProfilePicUrl = (state) => state.account.profilePicUrl;
export const selectAccountBalance = (state) => state.account.balance;

export const selectUserList = (state) => state.account.userList;
export const selectBoard = (state) => state.account.board;
export const selectForward = (state) => state.account.forward;
export const selectAccount = (state) => state.account.account;
export const selectIsRequesting = (state) => state.account.requesting;
export const selectError = (state) => state.account.error;
export const selectErrorStatus = (state) => state.account.errorStatus;

export const selectUserId = (state) => state.account.userId;
export const selectUserPicture = (state) => state.account.userPicUrl;
export const selectProfileId = (state) => state.account.profileId;
export const selectProfilePicUrl = (state) => state.account.profilePicUrl;
export const selectUsername = (state) => state.account.username;

export const selectEmail = (state) => state.account.email;
export const selectLang = (state) => state.account.lang;
export const selectTranslation = (state) => state.account.translation;
export const selectApiKey = (state) => state.account.apiKey;

export const selectSocialLinksItems = (state) => state.account.socialLinksItems;
export const selectIsShowStatus = (state) => state.account.isShowStatus;
export const selectFullName = (state) => state.account.fullName;

export const selectTemplates = (state) => state.account.templates;
export const selectThemes = (state) => state.account.themes;
export const selectPalettes = (state) => state.account.palettes;

export const selectWizardStatus = (state) => state.account.wizardStatus;
export const selectIsOpenUnlockModal = (state) => state.account.isOpenUnlockModal;

export const selectNotifs = (state) => state.account.notifs; // Settings
// --- Notification selectors ---
export const selectNotifications = (state) => state.account.notifications;
export const selectNotificationsItems = (state) => state.account.notifications.items;
export const selectNotificationsUnreadCount = (state) => state.account.notifications.unreadCount;
export const selectNotificationsUnreadByType = (state) => state.account.notifications.unreadByType;
export const selectNotificationsUnreadByEvent = (state) => state.account.notifications.unreadByEvent;
export const selectNotificationsLead = (state) => state.account.notifications.unreadByType?.['new-lead'] || 0;
// 'messages' = key from API unreadByEvent, 'new-message' = key from SignalR real-time
export const selectNotificationsMessage = (state) =>
  (state.account.notifications.unreadByEvent?.['messages'] || 0) + (state.account.notifications.unreadByEvent?.['new-message'] || 0);

export const selectIsDisplayPreviewStore = (state) => state.account.isDisplayPreviewStore;
export const selectStorePreviewProducts = (state) => state.account.storePreviewProducts;

export const selectPreviewQRCode = (state) => state.account.previewQRCode;
export const selectShareQRCode = (state) => state.account.shareQRCode;
export const selectAnnouncement = (state) => state.account.announcement;

export default accountSlice.reducer;
